import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Save, Printer as PrinterIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { useFirmSettingsStore } from '../../../store/firmSettingsStore';
import { useLabelStore } from '../../../store/labelStore';
import { usePrinterStore } from '../../../store/printerStore';
import { LabelCategory } from '../../../types/label';
import { IconTextButton } from '../../../components/ButtonComponents';

export const PROCESS_LIST = [
  { category: LabelCategory.PRODUCT, label: 'Ürün/Stok Etiketi' },
  { category: LabelCategory.SHIPPING, label: 'Sevk/Kargo Etiketi' },
  { category: LabelCategory.PICKING, label: 'Sipariş Toplama Etiketi' },
  { category: LabelCategory.PACKING, label: 'Sipariş Paketleme Etiketi' },
  { category: LabelCategory.RECEIPT, label: 'Fiş / Sipariş / Order' },
  { category: LabelCategory.WAYBILL, label: 'İrsaliye Çıktısı' },
  { category: LabelCategory.INVOICE, label: 'Fatura Çıktısı' },
  { category: LabelCategory.SHELF, label: 'Raf Etiketi' },
  { category: LabelCategory.WEIGHING, label: 'Tartım Etiketi' },
  { category: LabelCategory.OTHER, label: 'Diğer' },
];

export const PrintSettings = () => {
  const { fetchSettings, saveSettings, loading } = useFirmSettingsStore();
  const { data: labels, fetchDataDynamic: fetchLabels } = useLabelStore();
  const { data: printers, fetchDataDynamic: fetchPrinters } = usePrinterStore();

  const [firmSettings, setFirmSettings] = useState<any>({});
  const [printRouting, setPrintRouting] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchLabels();
    fetchPrinters();
    loadFirmSettings();
  }, []);

  const loadFirmSettings = async () => {
    const res = await fetchSettings();
    if (res) {
      setFirmSettings(res);
      const appSettings = typeof res.firmAppSettings === 'string' ? JSON.parse(res.firmAppSettings) : (res.firmAppSettings || {});
      setPrintRouting(appSettings.printSettings?.routing || {});
    }
  };

  const updateRouting = (category: string, printerId: any, templateId: any) => {
    setPrintRouting((prev: any) => ({
      ...prev,
      [category]: { printerId, templateId }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const currentAppSettings = typeof firmSettings.firmAppSettings === 'string' ? JSON.parse(firmSettings.firmAppSettings) : (firmSettings.firmAppSettings || {});
      
      const payload = {
        ...firmSettings,
        firmAppSettings: {
          ...currentAppSettings,
          printSettings: {
            ...(currentAppSettings.printSettings || {}),
            routing: printRouting
          }
        }
      };

      const success = await saveSettings(payload);
      if (success) {
        toast.success("Firma yazdırma yönlendirmeleri başarıyla kaydedildi.");
      } else {
        toast.error("Ayarlar kaydedilirken hata oluştu.");
      }
    } catch (e) {
      toast.error("Beklenmeyen bir hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const printerOptions = [
    { label: 'Varsayılan Sistem Yazıcısı', id: 'DEFAULT' },
    ...printers.map(p => ({ label: p.name || `Yazıcı ${p.id}`, id: p.id }))
  ];

  const templateOptions = [
    { name: 'Şablon Seçiniz...', id: '' },
    ...labels
  ];

  return (
    <div className="flex flex-col gap-6 p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/20 rounded-lg">
            <PrinterIcon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Firma Yazdırma Yönlendirmeleri</h1>
            <p className="text-sm text-text/70">Tüm şirket genelinde hangi işlemde hangi yazıcı ve şablonun kullanılacağını belirleyin.</p>
          </div>
        </div>
        <IconTextButton 
          onClick={handleSave} 
          icon={Save} 
          className="bg-primary text-white hover:bg-primaryHover shadow-sm px-4 py-2"
          disabled={isSaving || loading}
        >
          {isSaving || loading ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </IconTextButton>
      </div>

      <div className="bg-card rounded-xl shadow-sm border border-borderColor overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 bg-bgAlt border-b border-borderColor text-sm font-bold text-text uppercase tracking-wider">
          <div className="col-span-4">Operasyon Süreci</div>
          <div className="col-span-4">Hedef Yazıcı</div>
          <div className="col-span-4">Kullanılacak Şablon</div>
        </div>
        
        <div className="flex flex-col">
          {PROCESS_LIST.map((proc, index) => {
            const currentRoute = printRouting[proc.category] || { printerId: '', templateId: '' };
            
            return (
              <div 
                key={proc.category} 
                className={`grid grid-cols-12 gap-4 p-4 items-center ${
                  index !== PROCESS_LIST.length - 1 ? 'border-b border-borderColor' : ''
                } hover:bg-bgAlt transition-colors`}
              >
                <div className="col-span-4">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    <span className="font-semibold text-text">{proc.label}</span>
                  </div>
                  <span className="text-xs text-text/60 ml-4 font-mono">{proc.category}</span>
                </div>
                
                <div className="col-span-4">
                  <Dropdown 
                    value={currentRoute.printerId}
                    options={printerOptions}
                    optionLabel="label"
                    optionValue="id"
                    onChange={(e) => updateRouting(proc.category, e.value, currentRoute.templateId)}
                    placeholder="Yazıcı Seçin"
                    className="w-full"
                    filter
                    showClear
                  />
                </div>
                
                <div className="col-span-4">
                  <Dropdown 
                    value={currentRoute.templateId}
                    options={templateOptions}
                    optionLabel="name"
                    optionValue="id"
                    onChange={(e) => updateRouting(proc.category, currentRoute.printerId, e.value)}
                    placeholder="Şablon Seçin"
                    className="w-full"
                    filter
                    showClear
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
